import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, QrCode, Handshake, BarChart3, Wallet, LogOut, Loader2 } from "lucide-react";
import { Logo } from "../../components/joe/Badges";
import { api, store } from "../../lib/api";
import { homeFor } from "../Login";

const NAV = {
  admin: [["/office", "Dashboard", LayoutDashboard], ["/office/leads", "Leads & Cases", Users], ["/office/campaigns", "Campaigns & QR", QrCode],
    ["/office/partners", "Partners & Team", Handshake], ["/office/analytics", "Analytics", BarChart3], ["/office/compensation", "Referral Ledger", Wallet]],
  specialist: [["/office/leads", "My Cases", Users]],
  partner: [["/partner", "My Dashboard", LayoutDashboard], ["/partner/campaigns", "My Campaigns & QR", QrCode], ["/partner/analytics", "Performance", BarChart3]],
};

export const Panel = ({ title, children, right, className = "", testId }) => (
  <section className={`rounded-md border border-border bg-card p-4 ${className}`} data-testid={testId}>
    {title && <div className="mb-3 flex items-center justify-between gap-2"><h3 className="font-mono text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{title}</h3>{right}</div>}
    {children}
  </section>
);

export const Kpi = ({ l, v, accent, testId }) => (
  <div className="rounded-md border border-border bg-card p-3" data-testid={testId}>
    <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{l}</p>
    <p className={`mt-1 font-display text-2xl font-bold ${accent || ""}`}>{v ?? "—"}</p>
  </div>
);

export const Pill = ({ children, tone = "slate" }) => {
  const t = { slate: "bg-slate-800 text-slate-200", red: "bg-red-600/20 text-red-300 border-red-600/40", orange: "bg-orange-600/20 text-orange-300 border-orange-600/40",
    yellow: "bg-amber-500/20 text-amber-200 border-amber-500/40", green: "bg-emerald-600/20 text-emerald-300 border-emerald-600/40", cyan: "bg-cyan-600/20 text-cyan-300 border-cyan-600/40" }[tone];
  return <span className={`inline-flex items-center rounded border border-transparent px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase ${t}`}>{children}</span>;
};
export const urgTone = (c) => ({ RED: "red", ORANGE: "orange", YELLOW: "yellow", GREEN: "green" }[c] || "slate");

export default function OfficeLayout({ roles }) {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  useEffect(() => {
    if (!store.token) { nav("/login"); return; }
    api.get("/auth/me").then(({ data }) => (roles.includes(data.role) ? setUser(data) : nav(homeFor(data.role)))).catch(() => nav("/login"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!user) return <div className="office grid min-h-screen place-items-center bg-background"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const logout = () => { store.token = null; nav("/login"); };
  return (
    <div className="office min-h-screen bg-background text-foreground lg:flex">
      <aside className="border-b border-border bg-card lg:sticky lg:top-0 lg:h-screen lg:w-60 lg:shrink-0 lg:border-b-0 lg:border-r">
        <div className="flex items-center justify-between p-4"><Logo dark /><button data-testid="office-logout-btn" onClick={logout} className="text-muted-foreground hover:text-foreground lg:hidden"><LogOut className="h-4 w-4" /></button></div>
        <nav className="flex gap-1 overflow-x-auto px-2 pb-2 lg:flex-col lg:pb-0">
          {NAV[user.role].map(([to, l, Icon]) => (
            <NavLink key={to} to={to} end data-testid={`nav-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) => `flex shrink-0 items-center gap-2 rounded px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
              <Icon className="h-4 w-4" /> {l}
            </NavLink>
          ))}
        </nav>
        <div className="hidden p-4 lg:absolute lg:bottom-0 lg:block lg:w-60">
          <p className="truncate text-sm font-semibold" data-testid="office-user-name">{user.name}</p>
          <p className="font-mono text-[10px] uppercase text-muted-foreground">{user.role}{user.partner_id ? ` • ${user.partner_id}` : ""}</p>
          <button onClick={logout} data-testid="office-logout-btn-desktop" className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><LogOut className="h-3 w-3" /> Sign out</button>
        </div>
      </aside>
      <main className="min-w-0 flex-1 p-4 lg:p-6"><Outlet context={{ user }} /></main>
    </div>
  );
}
